import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import './PremiumPage.css';

/**
 * Premium subscription page component
 */
function PremiumPage() {
  const { isAuthenticated, isPremium, updateSubscription } = useContext(AuthContext);
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  // Handle subscription purchase
  const handleSubscribe = async () => {
    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/premium' } } });
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Simulate subscription update
      console.log(`Purchasing ${selectedPlan} plan`);

      // Simulate success after delay
      setTimeout(() => {
        setIsLoading(false);
        setSuccess(true);
      }, 1500);

    } catch (err) {
      console.error('Subscription error:', err);
      setError('Failed to process subscription. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="premium-page">
      <div className="premium-header">
        <h1>Upgrade to Premium</h1>
        <p className="premium-subtitle">
          Unlock advanced features and enjoy an ad-free experience
        </p>
      </div>

      {isPremium ? (
        <div className="already-premium">
          <h2>You're already a Premium member!</h2>
          <p>Thank you for your support. You have access to all premium features.</p>
          <Link to="/" className="home-button">
            Return to Home
          </Link>
        </div>
      ) : (
        <>
          {error && (
            <div className="premium-error">
              {error}
            </div>
          )}

          {success ? (
            <div className="premium-success">
              <h2>Subscription Successful!</h2>
              <p>Thank you for upgrading to Premium. You now have access to all premium features.</p>
              <Link to="/" className="home-button">
                Return to Home
              </Link>
            </div>
          ) : (
            <>
              <div className="plans-container">
                <div className="plans-toggle">
                  <button
                    className={`plan-toggle-btn ${selectedPlan === 'monthly' ? 'active' : ''}`}
                    onClick={() => handlePlanSelect('monthly')}
                  >
                    Monthly
                  </button>
                  <button
                    className={`plan-toggle-btn ${selectedPlan === 'yearly' ? 'active' : ''}`}
                    onClick={() => handlePlanSelect('yearly')}
                  >
                    Yearly <span className="save-badge">Save 20%</span>
                  </button>
                </div>

                <div className="plan-comparison">
                  <div className="plan-card free">
                    <div className="plan-header">
                      <h2>Free</h2>
                      <div className="plan-price">
                        <span className="price">$0</span>
                        <span className="period">forever</span>
                      </div>
                    </div>
                    <div className="plan-features">
                      <ul>
                        <li>5 projects</li>
                        <li>Basic templates</li>
                        <li>Standard file versioning</li>
                        <li>Export & import projects</li>
                        <li>Local storage only</li>
                        <li className="not-included">Ad-free experience</li>
                        <li className="not-included">Unlimited projects</li>
                        <li className="not-included">Advanced templates</li>
                        <li className="not-included">Priority support</li>
                      </ul>
                    </div>
                    <div className="plan-action">
                      <Link to="/" className="current-plan-btn">
                        Current Plan
                      </Link>
                    </div>
                  </div>

                  <div className="plan-card premium">
                    <div className="plan-badge">Recommended</div>
                    <div className="plan-header">
                      <h2>Premium</h2>
                      <div className="plan-price">
                        <span className="price">
                          {selectedPlan === 'monthly' ? '$9.99' : '$95.88'}
                        </span>
                        <span className="period">
                          {selectedPlan === 'monthly' ? '/ month' : '/ year'}
                        </span>
                      </div>
                    </div>
                    <div className="plan-features">
                      <ul>
                        <li>Unlimited projects</li>
                        <li>All templates including premium</li>
                        <li>Advanced file versioning</li>
                        <li>Export & import projects</li>
                        <li>Local storage & encrypted backup</li>
                        <li>Ad-free experience</li>
                        <li>Project collaboration (coming soon)</li>
                        <li>Priority support</li>
                        <li>Early access to new features</li>
                      </ul>
                    </div>
                    <div className="plan-action">
                      <button
                        className="upgrade-btn"
                        onClick={handleSubscribe}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Processing...' : 'Upgrade Now'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="premium-features">
                <h2>Premium Features</h2>
                <div className="features-grid">
                  <div className="feature-card">
                    <div className="feature-icon">🚀</div>
                    <h3>Unlimited Projects</h3>
                    <p>Create as many projects as you need with no restrictions</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon">📝</div>
                    <h3>Advanced Templates</h3>
                    <p>Access all premium templates including enterprise-grade applications</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon">🔄</div>
                    <h3>Advanced Versioning</h3>
                    <p>Enhanced version control with detailed change history</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon">🔒</div>
                    <h3>Encrypted Backup</h3>
                    <p>Secure cloud backup of your project data (coming soon)</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon">👨‍💻</div>
                    <h3>Priority Support</h3>
                    <p>Get help when you need it with priority response times</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon">🎯</div>
                    <h3>Ad-Free Experience</h3>
                    <p>Enjoy a clean, distraction-free development environment</p>
                  </div>
                </div>
              </div>

              <div className="premium-faq">
                <h2>Frequently Asked Questions</h2>
                <div className="faq-list">
                  <div className="faq-item">
                    <h3>How does payment work?</h3>
                    <p>
                      We use secure payment processing. You can pay with credit card, PayPal, or other payment methods.
                      Your subscription will automatically renew unless you cancel it.
                    </p>
                  </div>
                  <div className="faq-item">
                    <h3>Can I cancel anytime?</h3>
                    <p>
                      Yes, you can cancel your subscription at any time. You'll continue to have premium access until the end of your billing period.
                    </p>
                  </div>
                  <div className="faq-item">
                    <h3>Is my data secure with Premium?</h3>
                    <p>
                      Yes, all your project data remains private and secure. We use encryption for all backup operations
                      and never access your code without explicit permission.
                    </p>
                  </div>
                  <div className="faq-item">
                    <h3>What happens to my projects if I downgrade?</h3>
                    <p>
                      If you downgrade to the free plan, you'll still have access to your projects, but you'll be limited to 5 active projects.
                      You can export any extra projects before downgrading.
                    </p>
                  </div>
                </div>
              </div>

              <div className="premium-cta">
                <h2>Ready to Upgrade?</h2>
                <p>Get access to all premium features and enhance your development experience today.</p>
                <button
                  className="upgrade-btn large"
                  onClick={handleSubscribe}
                  disabled={isLoading}
                >
                  {isLoading ? 'Processing...' : 'Upgrade to Premium'}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default PremiumPage;